// src/components/dashboard/DashboardCard.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getIncidentSeverity } from "../../constants/severity";
import { getLaneForCategory } from "../../constants/lanes";
import { generateReportId } from "../../services/incidentReportService";
import { getStatusMeta } from "../../constants/incidentStatus";
import { formatDuration } from "../../utils/duration";
import { incidentAgeMs, isAwaitingFirstResponse } from "../../utils/queueMetrics";

/**
 * One assigned incident, as it appears on either dashboard.
 *
 * Two changes worth knowing about:
 *
 *  - The card used to print Urgency next to severity. Severity is *derived* from
 *    urgency (see constants/severity.js), so the two were the same fact twice,
 *    and the raw "medium" was the one the customer typed rather than the one we
 *    act on.
 *  - Every action was a solid filled button of equal weight, which gave "Cancel
 *    Problem" the same visual priority as "Open workspace". There is now one
 *    primary action per state; the rest recede.
 */

function DetailRow({ label, children }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-16 flex-shrink-0 text-slate-500">{label}</span>
      <span className="min-w-0 text-slate-900">{children}</span>
    </div>
  );
}

function DashboardCard({
  problem,
  userRole,
  onMarkComplete,
  onCancel,
  onRequestRefund,
  currentUserId,
}) {
  const navigate = useNavigate();
  const [showWorkspaceHint, setShowWorkspaceHint] = useState(false);

  const viewerRole = userRole === "helper" ? "expert" : "client";
  const isAssigned = Boolean(problem?.helperId && problem?.clientId);
  const canCall = isAssigned && problem.status !== "completed";

  // New schema with a fallback to the legacy field names.
  const expertSolved = Boolean(problem.expertSolved || problem.helperMarkedDone);
  const clientSolved = Boolean(problem.clientSolved || problem.clientMarkedDone);

  const awaitingViewerConfirmation =
    problem.status !== "completed" &&
    (viewerRole === "client"
      ? !clientSolved && expertSolved
      : !expertSolved && clientSolved);

  const storageKey = React.useMemo(
    () =>
      currentUserId
        ? `workspaceHintSeen_${currentUserId}_${problem?.id}`
        : `workspaceHintSeen_${problem?.id}`,
    [currentUserId, problem?.id]
  );

  useEffect(() => {
    try {
      if (!problem?.id) return;
      if (problem.status !== "assigned") return;
      if (!problem.helperId || !problem.clientId) return;
      if (!window.localStorage.getItem(storageKey)) setShowWorkspaceHint(true);
    } catch (error) {
      // Privacy mode can block localStorage; the hint is not worth failing over.
      console.warn("Unable to read workspace hint state:", error);
    }
  }, [problem?.id, problem?.status, problem?.helperId, problem?.clientId, storageKey]);

  const dismissHint = () => {
    try {
      if (problem?.id) window.localStorage.setItem(storageKey, "true");
    } catch (error) {
      console.warn("Unable to persist workspace hint state:", error);
    }
    setShowWorkspaceHint(false);
  };

  const severity = getIncidentSeverity(problem);
  const lane = getLaneForCategory(problem.categoryId || problem.category);
  const statusMeta = getStatusMeta(problem.status, viewerRole);
  const ageMs = incidentAgeMs(problem);

  const waitingOnViewer =
    (problem.nextActionOwner === "client" && viewerRole === "client") ||
    (problem.nextActionOwner === "expert" && viewerRole === "expert");

  const openWorkspace = () => {
    if (!problem.helperId || !problem.clientId) {
      toast.info(
        viewerRole === "client"
          ? "An expert has to pick this up before the workspace opens. Invite one of the suggested specialists, or wait for the queue."
          : "Claim this incident from your queue before opening the workspace."
      );
      return;
    }
    dismissHint();
    navigate(`/workspace/${problem.id}?forceWorkspace=1`);
  };

  const canConfirm =
    onMarkComplete &&
    [
      "in-progress",
      "assigned",
      "waiting_client_confirmation",
      "waiting_expert_confirmation",
    ].includes(problem.status);

  return (
    <div
      className={`flex flex-col rounded-xl border border-slate-200 border-l-2 bg-white p-5 transition hover:border-slate-300 ${severity.accentClass}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-slate-400">
          {generateReportId(problem.id)}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${severity.chipClass}`}
          title={severity.description}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${severity.dotClass}`}
            aria-hidden="true"
          />
          {severity.label}
        </span>
        <span
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusMeta.chipClass}`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${statusMeta.dotClass}`}
            aria-hidden="true"
          />
          {statusMeta.label}
        </span>
      </div>

      <h3 className="mt-2 text-base font-semibold text-slate-900">
        {problem.title}
      </h3>
      <p className="mt-1 line-clamp-2 text-sm text-slate-600">
        {problem.description}
      </p>

      <div className="mt-4 space-y-1.5">
        {lane && <DetailRow label="Lane">{lane.label}</DetailRow>}
        <DetailRow label="Open">{formatDuration(ageMs)}</DetailRow>
        {viewerRole === "client" && problem.helperName && (
          <DetailRow label="Expert">{problem.helperName}</DetailRow>
        )}
        {viewerRole === "expert" && problem.clientName && (
          <DetailRow label="Customer">{problem.clientName}</DetailRow>
        )}
      </div>

      {/* An expert owns this and has not spoken yet. Worth saying plainly to
          both parties rather than leaving the customer to wonder. */}
      {isAwaitingFirstResponse(problem) && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {viewerRole === "expert"
            ? "You have not sent a first response on this incident yet."
            : "Your expert has not sent their first response yet."}
        </p>
      )}

      {problem.nextAction && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {waitingOnViewer ? "Waiting on you" : "Next action"}
          </p>
          <p className="mt-0.5 text-sm text-slate-800">{problem.nextAction}</p>
        </div>
      )}

      {awaitingViewerConfirmation && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {viewerRole === "client"
            ? "Your expert marked this resolved. Confirm when you are satisfied and we will close it for both of you."
            : "The customer marked this resolved. Confirm once your work is complete and we will close it for both of you."}
        </p>
      )}

      {problem.tags?.length > 0 && (
        <p className="mt-3 truncate text-xs text-slate-500">
          {problem.tags.slice(0, 5).map((tag) => `#${tag}`).join(" ")}
          {problem.tags.length > 5 ? ` +${problem.tags.length - 5}` : ""}
        </p>
      )}

      {problem.status === "unresolved" && (
        <div className="mt-3 space-y-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-rose-900">
            Closed as unresolved —{" "}
            {viewerRole === "client"
              ? "you are eligible for a full refund."
              : "the customer is eligible for a full refund."}
          </p>
          {problem.refundEligible ? (
            <p className="text-xs font-medium text-rose-800">
              Refund requested. Operations has been notified.
            </p>
          ) : (
            onRequestRefund && (
              <button
                type="button"
                onClick={() => onRequestRefund(problem.id)}
                className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                {viewerRole === "client"
                  ? "Request refund"
                  : "Request refund for the customer"}
              </button>
            )
          )}
        </div>
      )}

      <div className="relative mt-4 flex flex-wrap items-center gap-2 pt-1">
        {showWorkspaceHint && (
          <div className="absolute -top-24 left-0 z-20 w-72 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
            <p className="text-xs font-semibold text-slate-900">
              Your workspace is ready
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Reopen it any time with{" "}
              <span className="font-semibold text-blue-700">Open workspace</span>{" "}
              on this card.
            </p>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={dismissHint}
                className="rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white transition hover:bg-slate-700"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={openWorkspace}
          className={`rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
            !problem.helperId || !problem.clientId
              ? "cursor-not-allowed opacity-50"
              : ""
          } ${showWorkspaceHint ? "ring-2 ring-blue-400 ring-offset-1" : ""}`}
          title={
            !problem.helperId || !problem.clientId
              ? "The incident must be assigned before the workspace opens"
              : undefined
          }
        >
          Open workspace
        </button>

        {canCall && (
          <button
            type="button"
            onClick={() =>
              navigate(`/workspace/${problem.id}?forceWorkspace=1&openCall=1`)
            }
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Start session
          </button>
        )}

        {canConfirm && (
          <button
            type="button"
            onClick={() => onMarkComplete(problem.id)}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              awaitingViewerConfirmation
                ? "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus:ring-emerald-500"
                : "border border-slate-300 text-slate-700 hover:bg-slate-50 focus:ring-blue-500"
            }`}
          >
            Mark resolved
          </button>
        )}

        {viewerRole === "client" && problem.status === "open" && onCancel && (
          <button
            type="button"
            onClick={() => onCancel(problem.id)}
            className="ml-auto rounded-lg px-2.5 py-2 text-sm font-medium text-slate-500 transition hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
            title="Withdraw this incident. Only possible while unclaimed."
          >
            Withdraw
          </button>
        )}
      </div>
    </div>
  );
}

export default DashboardCard;
